import type { StartConfig } from '../widgets/core/start';

export const handleActionTypes = ['hangup', 'zero', 'officeClosed'] as const;

export type HandleActionType = (typeof handleActionTypes)[number];

export type HandleActionMessage = string | Record<string, string>;

export type HandleAction = { type: HandleActionType; message?: HandleActionMessage };

export type HandleActionResult =
  | { type: 'hangup'; messages: HandleActionMessage[] }
  | { type: 'route'; nodeId: string; message?: HandleActionMessage }
  | undefined;

export const startActionOutputs = { zero: 'zeroPressed', officeClosed: 'officeClosed' } as const;

export function isZeroPressedConfigured(config: StartConfig | undefined) {
  return config?.enableZeroPressed === true;
}

export function isOfficeClosedConfigured(
  config: StartConfig | undefined,
): config is StartConfig & { businessHoursId: string } {
  return !!config?.businessHoursId;
}

type HandleActionNode = { id: string; data: Record<string, unknown> };

type HandleActionEdge = { source: string; target: string; sourceHandle?: string };

function readHandleAction(node: HandleActionNode | undefined, handleId: string): HandleAction | undefined {
  const handleActions = node?.data.handleActions as Record<string, HandleActionType | HandleAction> | undefined;
  const raw = handleActions?.[handleId];
  if (!raw) return undefined;

  return typeof raw === 'string' ? { type: raw } : raw;
}

function hangupResult(...messages: Array<HandleActionMessage | undefined>): HandleActionResult {
  return { type: 'hangup', messages: messages.filter((m) => m !== undefined) };
}

function withOuterMessage(result: HandleActionResult, message: HandleActionMessage | undefined): HandleActionResult {
  if (!result) return undefined;
  if (result.type === 'route') return { ...result, message };

  return hangupResult(message, ...result.messages);
}

export function resolveStartOutput(
  startNode: HandleActionNode | undefined,
  edges: HandleActionEdge[],
  handleId: string,
): HandleActionResult {
  if (!startNode) return undefined;

  const edge = edges.find((e) => e.source === startNode.id && e.sourceHandle === handleId);
  if (edge) return { type: 'route', nodeId: edge.target };

  const action = readHandleAction(startNode, handleId);
  return action?.type === 'hangup' ? hangupResult(action.message) : undefined;
}

export function resolveHandleAction(
  node: HandleActionNode,
  handleId: string,
  nodes: HandleActionNode[],
  edges: HandleActionEdge[],
  entryNodeId: string | undefined,
): HandleActionResult {
  const action = readHandleAction(node, handleId);
  if (!action) return undefined;

  if (action.type === 'hangup') return hangupResult(action.message);

  const startNode = nodes.find((n) => n.id === entryNodeId);
  const startConfig = startNode?.data.config as StartConfig | undefined;

  if (action.type === 'zero' && !isZeroPressedConfigured(startConfig)) return undefined;
  if (action.type === 'officeClosed' && !isOfficeClosedConfigured(startConfig)) return undefined;

  return withOuterMessage(resolveStartOutput(startNode, edges, startActionOutputs[action.type]), action.message);
}
